<script setup lang="ts">
const props = defineProps<{
  type:
    | "qr-invalid"
    | "stripe-error"
    | "campaign-paused"
    | "payment-link-invalid"
    | "campaign-invalid"
    | "payments-pending"
    | "stripe-not-configured";
  title: string;
  message: string;
}>();

const emit = defineEmits<{
  reload: [];
}>();

type IconConfig = {
  iconColor: string;
  icon: string;
  svg?: string;
};

const iconConfig: Record<typeof props.type, IconConfig> = {
  "qr-invalid": {
    iconColor: "text-red-500",
    icon: "i-lucide-qr-code",
  },
  "payment-link-invalid": {
    iconColor: "text-red-500",
    icon: "i-lucide-link-2-off",
  },
  "stripe-error": {
    iconColor: "text-amber-500",
    icon: "i-lucide-credit-card",
  },
  "campaign-paused": {
    iconColor: "text-orange-500",
    icon: "i-lucide-pause-circle",
  },
  // Cagnotte introuvable : le plus souvent un brouillon jamais publié. Surtout
  // PAS l'illustration Stripe — le compte de paiement n'est pas en cause.
  "campaign-invalid": {
    iconColor: "text-red-500",
    icon: "i-lucide-file-question",
  },
  // Cagnotte trouvée, paiements pas encore actifs : AUCUNE icône — le titre
  // suffit, et l'icône rouge laissait croire à une erreur (Karim 2026-10-08).
  "payments-pending": {
    iconColor: "",
    icon: "",
  },
  "stripe-not-configured": {
    svg: "/svg/hand-stripe.svg",
    iconColor: "",
    icon: "",
  },
};

const { t } = useI18n();

const config = computed(
  () => iconConfig[props.type] || iconConfig["qr-invalid"],
);

const buttonLabel = computed(() => {
  switch (props.type) {
    case "campaign-paused":
      return t("don.etatErreur.retourCagnotte");
    case "qr-invalid":
    case "payment-link-invalid":
    case "campaign-invalid":
    case "payments-pending":
      return t("don.etatErreur.rechargerPage");
    case "stripe-not-configured":
      // Le bouton RECHARGE la page — « plus tard » contredisait l'action
      // (Karim 2026-08-08). Le libellé doit décrire ce qui se passe au clic.
      return t("commun.actions.reessayer");
    default:
      return t("commun.actions.reessayer");
  }
});
</script>

<template>
  <div
    class="min-h-screen flex items-center justify-center bg-[#F2F2F7] dark:bg-black px-5 py-10"
  >
    <!-- iOS-style card -->
    <div class="w-full max-w-sm">
      <!-- Main card -->
      <div
        class="bg-white dark:bg-[#1C1C1E] rounded-[20px] shadow-[0_4px_24px_rgba(0,0,0,0.08)] dark:shadow-[0_4px_24px_rgba(0,0,0,0.4)] overflow-hidden"
      >
        <!-- Content -->
        <div class="px-6 py-10 text-center">
          <!-- Illustration SVG ou icône -->
          <div v-if="config.svg || config.icon" class="mb-6">
            <img
              v-if="config.svg"
              :src="config.svg"
              alt=""
              class="w-32 h-32 mx-auto"
            />
            <!-- Icône NUE : jamais de pastille de fond derrière une icône. -->
            <div v-else class="flex items-center justify-center">
              <UIcon
                :name="config.icon"
                class="w-14 h-14"
                :class="config.iconColor"
              />
            </div>
          </div>

          <!-- Title -->
          <h1
            class="text-[22px] font-semibold text-[#1C1C1E] dark:text-white mb-2 tracking-tight"
          >
            {{ title }}
          </h1>

          <!-- Message -->
          <p
            class="text-[15px] text-[#8E8E93] dark:text-[#98989D] leading-relaxed max-w-[280px] mx-auto"
          >
            {{ message }}
          </p>
        </div>

        <!-- Action button — noir Donoor (#212121), aligné avec les CTA
             principaux du site (bouton 'Je participe' campagne, etc.).
             On évite UButton color="primary" qui résout sur le palette green
             par défaut Nuxt UI (= bouton vert non-aligné brand). -->
        <div class="px-6 pb-6 pt-2">
          <button
            type="button"
            class="w-full py-3.5 rounded-full text-base font-semibold text-white bg-[#212121] hover:bg-[#333] active:bg-black transition-colors shadow-sm hover:shadow-md focus:outline-none focus:ring-2 focus:ring-[#212121]/40 dark:bg-white dark:text-[#212121] dark:hover:bg-gray-100 dark:active:bg-gray-200"
            @click="emit('reload')"
          >
            {{ buttonLabel }}
          </button>
        </div>
      </div>

      <!-- Secondary info (optional) -->
      <p
        class="text-center text-[13px] text-[#8E8E93] dark:text-[#98989D] mt-6 px-4"
      >
        {{ t('don.etatErreur.contacterAssociation') }}
      </p>
    </div>
  </div>
</template>
